import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { first } from 'lodash'

import Table from 'pages/Administration/components/Table'

import { deletePermission, getPermissions } from '../actions'
import { detailCellRendererParams, scenarioColumns } from '../config'
import { buildTableActions, formatRowData, formatTableData } from '../helpers'
import {
  getSimpleNodeId,
  excelStyles,
  sizeTableToFit,
  sendNotification
} from 'pages/Administration/helpers'

import { frameworkComponents } from 'pages/Administration/constants'
import { DELETE_ROW } from '../constant'
import { useAgGridTranslation } from 'utils/hooks/useDocumentTranslation'
import { openModal } from 'components/Modal/actions'

const ScenarioPanel = ({ roles, scenario }) => {
  const [gridApi, setGridApi] = useState(null)
  const [columnApi, setColumnApi] = useState(null)
  const [selectedRow, setSelectedRow] = useState(null)

  const { i18n, t } = useTranslation(['Administration', 'Common', 'Wizard'])

  useEffect(() => {
    getPermissions()
  }, [])

  const rowData = useMemo(() => formatTableData(scenario, t), [scenario, t])

  const onAdd = () => {
    openModal('administrationScenario', {
      roles: roles
    })
  }

  const onDelete = () => {
    openModal('confirmAction', {
      modalHeading: t('Administration:RolesManagement.DeleteRole'),
      message: t('Common:RoleDeletionConfirmation'),
      onConfirm: async () => {
        try {
          await deletePermission(selectedRow.name)
          resetSelection()

          sendNotification({ t, kind: 'success', info: DELETE_ROW })
        } catch (err) {
          console.error(err)
        }
      },
      loadingOverlay: true
    })
  }

  const actions = useMemo(() => {
    if (gridApi && columnApi) {
      return buildTableActions({
        t,
        gridApi,
        columnApi,
        onAdd,
        onDelete
      })
    }
  }, [t, gridApi, columnApi, onAdd, onDelete])

  const onCellClicked = ({ data: { data: originalData } }) => {
    openModal('administrationScenario', {
      scenarioValue: formatRowData(originalData),
      roles: roles
    })
  }

  const onGridReady = ({ api, columnApi }) => {
    setGridApi(api)
    setColumnApi(columnApi)
    sizeTableToFit(api)
  }

  const { t: agGridT, destroyed } = useAgGridTranslation()

  const placeholderCellClicked = () => {}

  const resetSelection = () => {
    setSelectedRow({})
    gridApi.deselectAll()
  }

  return (
    <div className='role-view'>
      <div className='children-view'>
        <h3 className='no-action-view'>
          {t(`ScenarioManagement.TitleManager`)}
        </h3>
        <Table
          masterDetail
          rowData={rowData}
          headerActions={actions}
          getRowNodeId={getSimpleNodeId}
          excelStyles={excelStyles}
          frameworkComponents={frameworkComponents}
          columnDefs={scenarioColumns(t)}
          detailCellRendererParams={detailCellRendererParams(
            t,
            placeholderCellClicked
          )}
          onCellClicked={onCellClicked}
          onGridReady={onGridReady}
          onSelectionChanged={(rows) => setSelectedRow(first(rows) ?? {})}
          localeTextFunc={agGridT}
          suppressExcelExport
          suppressCsvExport
        />
      </div>
    </div>
  )
}

export default ScenarioPanel
